<template>
  <div>
    <Header />
    <v-container>
      <v-row justify="center" align="center" fill-height>
        <v-col cols="12" xl="8">
          <v-card :loading="loading">
            <v-alert v-if="error" type="error">{{error}}</v-alert>
            <v-container>
              <v-card-title>
                Abrechnung {{ app.name }} | {{ customer.name }}
                <div class="flex-grow-1"></div>
                <v-text-field v-model="search" append-icon="mdi-magnify" label="Suche" single-line></v-text-field>
              </v-card-title>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col>
                      <v-btn color="primary" @click="addInvoice">Abrechnung erstellen</v-btn>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-data-table
                        :headers="headers"
                        :items="invoices"
                        :search="search"
                        :items-per-page="50"
                        class="elevation-1"
                      >
                        <template
                          v-slot:item.dateStart="{item}"
                        >{{item.dateStart | moment("DD.MM.YYYY")}}</template>
                        <template
                          v-slot:item.dateEnd="{item}"
                        >{{item.dateEnd | moment("DD.MM.YYYY")}}</template>
                        <template v-slot:item.total="{item}">{{(item.total / 100).toFixed(2)}} €</template>

                        <template v-slot:item.state="{item}">
                          <v-chip v-if="item.state == 'unpaid'" class="error">unbezahlt</v-chip>
                          <v-chip v-if="item.state == 'paid'" class="success">bezahlt</v-chip>
                          <v-chip v-if="item.state == 'unknown'">unbekannt</v-chip>
                        </template>

                        <template v-slot:item.pdf="{item}">
                          <v-btn icon color="primary" v-if="item.pdf">
                            <a :href="'https://acs.accelery.de/uploads/invoices/' +item.id+ '/download'" download>
                              <v-icon>mdi-file-document</v-icon>
                            </a>
                          </v-btn>
                          <div v-else> 
                          <v-btn small v-if="!isACDPartner()" @click="openInvoiceDialog(item.id, false)">Rechnung ACC erzeugen</v-btn>
                          <v-btn small v-else @click="openInvoiceDialog(item.id, true)">Rechnung ACD erzeugen</v-btn>
                          </div>
                        </template>
                        <template v-slot:item.orders="{item}">
                          <div v-if="item.csv_generated">
                            <v-btn small :href="'https://acs.accelery.de/uploads/invoices/' + item.id + '/csv/overview'" download>
                              <v-icon left>mdi-file-download</v-icon>CSV Übersicht
                            </v-btn>
                            <v-btn small :href="'https://acs.accelery.de/uploads/invoices/' + item.id + '/csv/details'" download class="ml-2">
                              <v-icon left>mdi-file-download</v-icon>CSV Details
                            </v-btn>
                          </div>
                          <div v-else>
                            <v-btn small color="primary" @click="generateCSV(item.id)" :loading="loading">
                              <v-icon left>mdi-file-plus</v-icon>CSV Generieren
                            </v-btn>
                          </div>
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-container>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="invoiceOrdersDialog" width="640">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>Bestellungen</v-card-title>

        <v-card-text>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">#</th>
                  <th class="text-left">Externe ID</th>
                  <th class="text-left">Datum</th>
                  <th class="text-left">Versandart</th>
                  <th class="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in invoiceOrders" :key="item.name">
                  <td>{{ item.id }}</td>
                  <td>{{ item.reference }}</td>
                  <td>{{ item.date | moment("DD.MM.YYYY")}}</td>
                  <td>{{ item.carrier }}</td>
                  <td>{{ item.status }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="invoiceOrdersDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


    <v-dialog v-model="invoiceDialog" max-width="600px">
    <v-card>
      <v-card-title>
        <span class="headline">ERP Rechnung erstellen</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="invoiceCreationPayload.name"
                label="Title"
                disabled
                outlined
                aria-disabled="true"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="invoiceCreationPayload.invoiceId"
                label="Rechnungsnummer"
                disabled
                outlined
                aria-disabled="true"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="invoiceCreationPayload.customerId"
                label="Kundennummer"
                disabled
                outlined
                aria-disabled="true"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model.number="invoiceCreationPayload.total"
                label="Gesamtbetrag"
                disabled
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model.number="invoiceCreationPayload.vatRate"
                label="Steuersatz (%)"
                type="number"
                outlined
                required
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="invoiceCreationPayload.note"
                :items="invoiceNoteOptions"
                label="Notiz"
                outlined
                clearable
              >
                <template v-slot:append-outer>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        @click="customNoteDialog = true"
                      >
                        mdi-pencil
                      </v-icon>
                    </template>
                    <span>Benutzerdefinierte Notiz hinzufügen</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="invoiceDialog = false">Abbrechen</v-btn>
        <v-btn color="blue darken-1" text @click="createInvoice">Erstellen</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Dialog for custom note input -->
  <v-dialog v-model="customNoteDialog" max-width="400px">
    <v-card>
      <v-card-title>
        <span class="headline">Benutzerdefinierte Notiz</span>
      </v-card-title>
      <v-card-text>
        <v-textarea
          v-model="customNote"
          label="Geben Sie Ihre benutzerdefinierte Notiz ein"
          rows="3"
        ></v-textarea>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="customNoteDialog = false">Abbrechen</v-btn>
        <v-btn color="blue darken-1" text @click="addCustomNote">Hinzufügen</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>


  </div>
</template>

<script>
import AppService from "@/services/AppService";
import CustomerSerivce from "@/services/CustomerService";
import Header from "@/views/general/header";
import router from "@/plugins/router";

export default {
  name: "dashboard",
  components: {
    Header: Header
  },
  data: () => ({
    loading: true,
    error: null,
    app: {},
    customer: {},
    invoices: [],
    search: null,
    invoiceOrders: [],
    invoiceOrdersDialog: false,
    headers: [
      { text: "#", value: "id", sotable: true, align: "left" },
      { text: "Titel", value: "name", sotable: true, align: "left" },
      { text: "Start Datum", value: "dateStart", sotable: true, align: "left" },
      { text: "End Datum", value: "dateEnd", sotable: true, align: "left" },
      { text: "Betrag", value: "total", sotable: true, align: "left" },
      { text: "Status", value: "state", sotable: true, align: "left" },
      { text: "PDF", value: "pdf", sotable: true, align: "left" },
      { text: "Bestellungen", value: "orders", sotable: true, align: "left" },
      { text: "Aktion", value: "action", sotable: false, align: "left" }
    ],

    invoiceDialog: false,
    invoiceId: null,
    invoiceCreationPayload: {
      invoiceId: null,
      customerId: null,
      note: null,
      total: null,
      vatRate: null,
    },
    invoiceNoteOptions: ["", "steuerfreie innergemeinschaftliche Lieferung (EU) nach § 4 Nr. 1b UStG", "steuerfreie Ausfuhrlieferung nach § 4 Nr. 1a UStG"],
    customNoteDialog: false,
    customNote: '',
  }),
  mounted() {
    this.refresh();
  },
  methods: {

    isACDPartner() {
      return this.app.acdPartner == 1;
    },

    addInvoice() {
      router.push({
        name: "CustomersInvoicesAdd",
        params: {
          appId: this.$route.params.appId,
          customerId: this.$route.params.customerId
        }
      });
    },

    openInvoiceDialog(invoiceId, isACD) {
      this.invoiceId = invoiceId;
      this.invoiceCreationPayload.invoiceId = invoiceId;
      this.invoiceCreationPayload.customerId = this.$route.params.customerId;
      let invoice = this.invoices.find(i => i.id == invoiceId); 
      this.invoiceCreationPayload.name = invoice.name;
      this.invoiceCreationPayload.total = invoice.total / 100;
      this.invoiceCreationPayload.acd = isACD;

      this.invoiceDialog = true;
    },

    addCustomNote() {
      if (this.customNote) {
        this.invoiceNoteOptions.push(this.customNote);
        this.invoiceCreationPayload.note = this.customNote;
        this.customNote = '';
      }
      this.customNoteDialog = false;
    },

    async createInvoice() {
      this.loading = true;
      try {
        await CustomerSerivce.createERPInvoice(
          this.$route.params.appId,
          this.$route.params.customerId,
          this.invoiceCreationPayload
        );
        this.invoiceDialog = false;
        this.refresh();
      } catch (err) {
        alert("Can't create invoice.");
      } finally {
        this.loading = false;
      }
    },

    async generateCSV(invoiceId) {
      this.loading = true;
      try {
        await CustomerSerivce.generateInvoiceCSV(
          this.$route.params.appId,
          this.$route.params.customerId,
          invoiceId
        );
        await this.refresh(); // Aktualisiere die Ansicht um die neuen CSV-Pfade zu laden
      } catch (err) {
        console.error('Fehler bei CSV-Generierung:', err);
        this.$emit('show-error', 'Die CSV-Dateien konnten nicht generiert werden.');
      } finally {
        this.loading = false;
      }
    },

    async loadOrdersForInvoice(invoiceId) {
      this.loading = true;
      try {
        this.invoiceOrders = (
          await CustomerSerivce.getInvoiceOrders(
            this.$route.params.appId,
            this.$route.params.customerId,
            invoiceId
          )
        ).data;

        this.invoiceOrdersDialog = true;
      } catch (err) {
        alert("Can't load orders of invoice.");
      } finally {
        this.loading = false;
      }
    },

    async refresh() {
      this.loading = true;
      try {
        this.app = (await AppService.getApp(this.$route.params.appId)).data;
        if (!this.app) {
          this.app = { name: "APP NICHT GEFUNDEN" };
          router.back();
        }

        this.customer = (
          await CustomerSerivce.getCustomer(
            this.$route.params.appId,
            this.$route.params.customerId
          )
        ).data;
        if (!this.customer) {
          router.back();
        }

        this.invoices = (
          await CustomerSerivce.getInvoices(
            this.$route.params.appId,
            this.$route.params.customerId
          )
        ).data;

        this.loading = false;
      } catch (err) {
        this.loading = false;
        this.error = err.message;
        if (err.response.status == 401) {
          router.replace("/auth/login");
        }
      }
    }
  }
};
</script>

